<template>
  <div class="table-responsive" :style="wrapperStyle">
    <table class="table trips-table" :class="tableClass">
      <thead class="thead-light">
        <tr>
          <th
            v-for="(column, index) in columns"
            :key="index"
            v-if="
              typeof column.show === 'function'
                ? column.show()
                : column.show !== false
            "
            scope="col"
          >
            {{ column.label }}
          </th>
          <th
            v-if="hasActions"
            scope="col"
            :class="{ 'has-actions': hasActions }"
          >
            {{ Lang.get("messages.actions") }}
          </th>
        </tr>
      </thead>

      <tbody>
        <!-- Loading State (Shimmer) -->
        <template v-if="loading">
          <tr v-for="n in 5" :key="'shimmer-' + n" class="shimmer-row">
            <td
              v-for="(column, index) in columns"
              :key="index"
              v-if="
                typeof column.show === 'function'
                  ? column.show()
                  : column.show !== false
              "
            >
              <div class="shimmer-box"></div>
            </td>
            <td v-if="hasActions">
              <div class="shimmer-box" style="width: 80px"></div>
            </td>
          </tr>
        </template>

        <!-- Data State -->
        <template v-else>
          <!-- Empty State -->
          <tr class="table-secondary" v-if="!items || items.length === 0">
            <td
              class="text-center"
              :colspan="columns.length + (hasActions ? 1 : 0)"
            >
              {{ emptyMessage || Lang.get("messages.no_results") }}
            </td>
          </tr>

          <!-- Data Rows -->
          <tr
            v-for="(item, index) in items"
            :key="item[rowKey] || index"
            :class="getRowClasses(item)"
          >
            <td
              v-for="(column, colIndex) in columns"
              :key="colIndex"
              v-if="
                typeof column.show === 'function'
                  ? column.show(item)
                  : column.show !== false
              "
              :class="column.tdClass"
              :style="column.tdStyle"
            >
              <!-- Special case for row number -->
              <template v-if="column.key === '#'">
                {{ index + 1 }}
              </template>

              <!-- Custom slots: `column-key` or `key` -->
              <template v-else-if="$scopedSlots[`column-${column.key}`]">
                <slot
                  :name="`column-${column.key}`"
                  :item="item"
                  :value="item[column.key]"
                />
              </template>
              <template v-else-if="$scopedSlots[column.key]">
                <slot
                  :name="column.key"
                  :item="item"
                  :value="item[column.key]"
                />
              </template>

              <!-- Fallback to plain value -->
              <template v-else>
                {{ item[column.key] }}
              </template>
            </td>

            <!-- Actions / Dropdowns -->
            <td v-if="hasActions">
              <div class="actions-wrapper d-flex align-items-center flex-wrap">
                <slot
                  name="actions"
                  :item="item"
                  :buttonClass="buttonClass"
                  :buttonIcons="buttonIcons"
                />
                <slot name="dropdown" :item="item" />
              </div>
            </td>
          </tr>
        </template>
      </tbody>

      <!-- Optional footer slot -->
      <tfoot v-if="$slots.footer || $scopedSlots.footer">
        <slot name="footer"></slot>
      </tfoot>
    </table>
  </div>
</template>

<script>
export default {
  name: "BaseTable",
  props: {
    columns: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    rowKey: {
      type: String,
      default: "id", // supports custom row keys
    },
    rowClass: {
      type: [String, Function],
      default: "",
    },
    wrapperStyle: {
      type: String,
      default: "",
    },
    tableClass: {
      type: [String, Array, Object],
      default: "",
    },
    emptyMessage: {
      type: String,
      default: "", // fallback to Lang in template
    },
    buttonClass: {
      type: String,
      default: "custom-btn d-flex", // Default class for buttons
    },
    buttonIcons: {
      type: Object,
      default: () => ({
        view: "fas fa-eye",
        edit: "fas fa-pencil-alt",
        delete: "fas fa-trash-alt",
        access: "fas fa-sign-in-alt",
      }),
    },
  },
  computed: {
    hasActions() {
      return (
        !!this.$scopedSlots.actions ||
        !!this.$slots.actions ||
        !!this.$scopedSlots.dropdown ||
        !!this.$slots.dropdown
      );
    },
  },
  methods: {
    getRowClasses(item) {
      return typeof this.rowClass === "function"
        ? this.rowClass(item)
        : this.rowClass;
    },
  },
};
</script>

<style scoped>
/* Last column for actions should take minimal width */
.table th.has-actions {
  width: 1%;
  white-space: nowrap;
}

/* Shimmer Effect */
.shimmer-box {
  height: 20px;
  width: 100%;
  background: #f6f7f8;
  background-image: linear-gradient(
    to right,
    #f6f7f8 0%,
    #edeef1 20%,
    #f6f7f8 40%,
    #f6f7f8 100%
  );
  background-repeat: no-repeat;
  background-size: 800px 100%;
  border-radius: 4px;
  animation: shimmer 1s linear infinite forwards;
}

@keyframes shimmer {
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
}
</style>
